import { useEffect, useRef } from 'react';
import {
  ResolveRestoreTargetParams,
  UseListboxScrollAnchorParams,
} from '../asyncAutocomplete.types';

const isRememberedPositionValid = (listbox: HTMLElement, isSameList: boolean) =>
  isSameList && listbox.isConnected;

const applyRestore = (listbox: HTMLElement, target: number) => {
  const scrollTopBefore = listbox.scrollTop;
  listbox.scrollTop = target;
  return listbox.scrollTop !== scrollTopBefore;
};

const resolveRestoreTarget = ({
  listbox,
  lastScrollTop,
  isExtending,
}: ResolveRestoreTargetParams) => {
  if (!isExtending || lastScrollTop === null) {
    return null;
  }

  const wasMovedBackwards = listbox.scrollTop < lastScrollTop;
  return wasMovedBackwards ? lastScrollTop : null;
};

export const useListboxScrollAnchor = ({
  optionCount,
  listKey,
  isLoading,
}: UseListboxScrollAnchorParams) => {
  const listboxRef = useRef<HTMLElement | null>(null);
  const lastScrollTopRef = useRef<number | null>(null);
  const isRestoringRef = useRef(false);
  const previousCountRef = useRef(optionCount);
  const previousListKeyRef = useRef(listKey);

  useEffect(() => {
    const listbox = listboxRef.current;
    const isSameList = listKey === previousListKeyRef.current;
    const isExtending = isLoading || optionCount > previousCountRef.current;

    previousCountRef.current = optionCount;
    previousListKeyRef.current = listKey;

    if (!listbox) {
      return;
    }

    if (!isRememberedPositionValid(listbox, isSameList)) {
      listboxRef.current = null;
      lastScrollTopRef.current = null;
      isRestoringRef.current = false;
      return;
    }

    const restoreTarget = resolveRestoreTarget({
      listbox,
      isExtending,
      lastScrollTop: lastScrollTopRef.current,
    });

    if (restoreTarget === null) {
      return;
    }

    isRestoringRef.current = applyRestore(listbox, restoreTarget);
  }, [optionCount, listKey, isLoading]);

  const trackListboxScroll = (listbox: HTMLElement) => {
    listboxRef.current = listbox;

    const isEchoOfRestore = isRestoringRef.current;
    isRestoringRef.current = false;

    if (isEchoOfRestore) {
      return false;
    }

    lastScrollTopRef.current = listbox.scrollTop;
    return true;
  };

  return { trackListboxScroll };
};
